import React, { Suspense, lazy, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Archive, Database, Trash2 } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import apiReminderBuilder from "../../api";
import apiReminderSource from "./api";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { loadSourceFilterDependencies } from "./features/source.dependencies";
import { sourceFilterFields } from "./features/source.filter";
import { errorMessage } from "../../features/builder.utils";
import { getSourceColumns } from "./source-list/sourceColumns";
import SourceRowActions from "./source-list/SourceRowActions";
import SourceViewDrawer from "./source-dialogs/SourceViewDrawer";
import UsedByDrawer from "./source-dialogs/UsedByDrawer";

// The editor and the preview are only needed once opened.
const SourceEditor = lazy(() => import("./source-editor/SourceEditor"));
const PreviewRowsDialog = lazy(() => import("./source-dialogs/PreviewRowsDialog"));

const INITIAL_QUERY = { page: 1, size: 10, searchText: "", search: {}, sort: { key: "id", direction: "desc" } };

// Reminder Setup -> Source (tbl_rem_source): the tables reminders may read and the columns they may use.
const Source = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;

  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(INITIAL_QUERY);
  const [reloadKey, setReloadKey] = useState(0);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [selectionReset, setSelectionReset] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editor, setEditor] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [usedBy, setUsedBy] = useState(null);
  const [previewOf, setPreviewOf] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmData, setConfirmData] = useState({ isOpen: false });

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const hasQuery = Boolean(query.searchText.trim()) || Object.keys(query.search).length > 0;

  // ── Data Fetching ─────────────────────────────────────────────────────────

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiReminderSource
      .getAll({
        searchText: query.searchText.trim(),
        search: JSON.stringify(query.search),
        limit: query.size,
        skip: (query.page - 1) * query.size,
        sortBy: query.sort.key,
        sortOrder: query.sort.direction,
      })
      .then((res) => {
        if (!active) return;
        setRecords((res.data?.data || []).map((row, index) => ({ ...row, __index: index })));
        setTotalCount(res.data?.total_count || 0);
      })
      .catch((err) => active && showAlert({ type: "error", message: errorMessage(err, "Failed to load sources.") }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [query, reloadKey, showAlert]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSearch = (searchText) => setQuery((current) => ({ ...current, searchText, page: 1 }));
  const handleApplyFilters = ({ search }) => setQuery((current) => ({ ...current, search: search || {}, page: 1 }));
  const handlePageChange = (page, newSize) => setQuery((current) => (newSize && newSize !== current.size ? { ...current, page: 1, size: newSize } : { ...current, page }));
  const handleSort = (key) =>
    setQuery((current) => ({ ...current, page: 1, sort: { key, direction: current.sort.key === key && current.sort.direction === "asc" ? "desc" : "asc" } }));

  const handleRefresh = () => {
    setQuery({ ...INITIAL_QUERY });
    setResetTrigger((key) => key + 1);
    setSelectionReset((key) => key + 1);
  };

  const openConfirm = (options) => new Promise((resolve) => setConfirmData({ ...options, isOpen: true, resolve }));
  const handleConfirmClose = (result) => {
    confirmData.resolve?.(result);
    setConfirmData({ isOpen: false });
  };

  /** Runs a row request with its busy spinner. */
  const withBusy = async (row, action, run) => {
    setBusy({ id: row.id, action });
    try {
      await run();
    } finally {
      setBusy(null);
    }
  };

  const openView = (row) =>
    withBusy(row, "view", async () => {
      try {
        const res = await apiReminderBuilder.getSource(row.id);
        setViewRecord(res.data?.data ?? null);
      } catch (err) {
        showAlert({ type: "error", message: errorMessage(err, "Could not load this source.") });
      }
    });

  const openUsedBy = (row) =>
    withBusy(row, "used-by", async () => {
      try {
        const res = await apiReminderSource.getUsedBy(row.id);
        setUsedBy({ source: row, data: res.data?.data ?? null });
      } catch (err) {
        showAlert({ type: "error", message: errorMessage(err, "Could not load where this source is used.") });
      }
    });

  const handlePublish = (row) =>
    withBusy(row, "publish", async () => {
      try {
        const res = await apiReminderSource.publish(row.id);
        const warnings = res.data?.data?.warnings ?? [];
        showAlert({ type: warnings.length ? "warning" : "success", message: [res.data?.message || "Published.", ...warnings].join(" ") });
      } catch (err) {
        const problems = err.response?.data?.data?.problems ?? [];
        const more = problems.length > 1 ? ` (+${problems.length - 1} more, see View)` : "";
        showAlert({ type: "error", title: "Cannot publish yet", message: `${errorMessage(err)}${more}` });
      } finally {
        // A refused publish stores its problems on the source, so the list refreshes either way.
        reload();
      }
    });

  const handleArchive = async (row) => {
    const confirmed = await openConfirm({
      title: "Archive Source",
      message: `Archive "${row.label}"? Archived sources are no longer offered to new triggers. A source used by an active trigger can't be archived.`,
      confirmText: "Archive",
      cancelText: "Cancel",
      variant: "warning",
      icon: <Archive size={20} />,
    });
    if (!confirmed) return;
    await withBusy(row, "archive", async () => {
      try {
        const res = await apiReminderSource.archive(row.id);
        showAlert({ type: "success", message: res.data?.message || "Archived." });
        reload();
      } catch (err) {
        showAlert({ type: "error", message: errorMessage(err, "Archive failed.") });
      }
    });
  };

  const deleteSources = async (ids) => {
    const confirmed = await openConfirm({
      title: ids.length > 1 ? "Delete Selected" : "Delete Source",
      message: `Delete ${ids.length > 1 ? `${ids.length} sources` : "this source"} and their fields? Sources used by triggers or domain events can't be deleted.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
      icon: <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiReminderSource.delete(ids);
      showAlert({ type: "success", message: res.data?.message || "Deleted successfully." });
      setSelectionReset((key) => key + 1);
      reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err, "Delete failed.") });
    }
  };

  const handleCreate = () => setEditor({ id: null });
  const closeEditor = useCallback(
    (changed) => {
      setEditor(null);
      if (changed) reload();
    },
    [reload]
  );

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = useMemo(() => getSourceColumns((query.page - 1) * query.size), [query.page, query.size]);

  const renderRowActions = (row) => (
    <SourceRowActions
      row={row}
      canManage={canManage}
      busy={busy}
      onView={openView}
      onEdit={(target) => setEditor({ id: target.id })}
      onPreview={setPreviewOf}
      onUsedBy={openUsedBy}
      onPublish={handlePublish}
      onArchive={handleArchive}
      onDelete={(target) => deleteSources([target.id])}
    />
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <TableToolBar
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          onCreate={handleCreate}
          onDeleteSelected={canManage ? () => deleteSources(selectedRows) : undefined}
          selectedCount={selectedRows.length}
          resetSearchTrigger={resetTrigger}
          canCreate={canManage}
          resultCount={totalCount}
          advanceFilter={
            <GlobalAdvanceFilter fields={sourceFilterFields} dependenciesLoader={loadSourceFilterDependencies} onApply={handleApplyFilters} resetKey={resetTrigger} />
          }
        />

        {!loading && records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={Database}
              title={hasQuery ? "No sources match" : "No reminder sources yet"}
              message={
                hasQuery
                  ? "No sources match your search or filters. Try adjusting them."
                  : "A source registers a table reminders may read, and the columns they may use."
              }
              showBack={false}
              showRefresh={hasQuery}
              onRefresh={handleRefresh}
              showCreate={canManage && !hasQuery}
              createText="Create Source"
              onCreate={handleCreate}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={records}
                loading={loading}
                columns={columns}
                pageSize={query.size}
                sortConfig={query.sort}
                onSort={handleSort}
                onSelectionChange={setSelectedRows}
                resetSelectionTrigger={selectionReset}
                selectable={canManage}
                showActions
                renderActions={renderRowActions}
              />
            </div>
            <Pagination totalCount={totalCount} pageSize={query.size} currentPage={query.page} onPageChange={handlePageChange} selectedCount={selectedRows.length} />
          </>
        )}
      </div>

      <Suspense fallback={null}>
        {editor && <SourceEditor sourceId={editor.id} onClose={closeEditor} />}
        {previewOf && <PreviewRowsDialog source={previewOf} onClose={() => setPreviewOf(null)} />}
      </Suspense>

      {viewRecord && <SourceViewDrawer record={viewRecord} onClose={() => setViewRecord(null)} />}
      {usedBy && <UsedByDrawer source={usedBy.source} usedBy={usedBy.data} onClose={() => setUsedBy(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default Source;
